import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  Collapse,
  Skeleton,
  Tabs,
  Tab,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQueryClient } from "@tanstack/react-query";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import theme, { useAppTheme } from "@/utils/theme";
import { formatInBackofficeLondon } from "@/utils/dateUtils";

dayjs.extend(utc);
import {
  Enrollment,
  EnrollmentStatus,
  TransactionType,
} from "@/domain/enrollment.interface";
import EnrollmentMenu from "@/ui/menus/clients/EnrollmentMenu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { a11yProps } from "@/utils/a11yProps";
import DisenrollProgramModal from "@/ui/modals/clients/DisenrollProgramModal";
import TabPanel from "@/components/TabPanel";
import TestResultsTab from "../participants/tabs/TestResultsTab";
import { formatCurrency, getLocaleByCountry } from "@/utils/formatters";
import {
  useCountrySetting,
  useCurrencySetting,
  useGetSettings,
} from "@/hooks/useSetting";
import IncentiveHistoryTab from "../participants/tabs/IncentiveHistoryTab";
import ParticipantEditProgramModal from "@/ui/modals/participants/ParticipantEditProgramModal";
import StatisticsEmbeds from "@/ui/content/clients/StatisticsEmbeds";
import { useUserStore } from "@/hooks/useUserStore";
import { formatEnrollmentStatus } from "@/services/utils/enrollmentStatus.utils";
import { toTitleCase } from "@/utils/toTitleCase";
import { useTranslation } from "react-i18next";

interface EnrollmentListProps {
  enrollments?: Enrollment[];
  isLoadingEnrollments: boolean;
  refetchEnrollments: () => void;
  participantName?: string;
  handleViewImages: (
    testResultId: string,
    enrollmentId: string,
    enrollmentStatus: EnrollmentStatus,
    validationType: string,
  ) => void;
  onRefetchTestResults: (refetchFn: () => void) => void;
  refetchParticipant: () => void;
  handleEnrollToProgram?: () => void;
  handleOnDemandIncentive: (enrollment: Enrollment) => void;
}

const EnrollmentList: React.FC<EnrollmentListProps> = ({
  enrollments,
  isLoadingEnrollments,
  refetchEnrollments,
  participantName,
  handleViewImages,
  onRefetchTestResults,
  refetchParticipant,
  handleEnrollToProgram,
  handleOnDemandIncentive,
}) => {
  const queryClient = useQueryClient();
  const [isDisenrollProgramModalOpen, setIsDisenrollProgramModalOpen] =
    useState(false);
  const [isEditProgramModalOpen, setIsEditProgramModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment>();

  const handleCloseDisenrollProgramModal = () => {
    refetchParticipant();
    refetchEnrollments();
    if (selectedEnrollment?._id) {
      queryClient.invalidateQueries({
        queryKey: ["coyn-test-results", selectedEnrollment._id],
      });
    }
    setSelectedEnrollment(undefined);
    setIsDisenrollProgramModalOpen(false);
  };

  const handleDisenroll = (enrollment: Enrollment) => {
    setSelectedEnrollment(enrollment);
    setIsDisenrollProgramModalOpen(true);
  };

  const handleEditEnrollment = (enrollment: Enrollment) => {
    setSelectedEnrollment(enrollment);
    setIsEditProgramModalOpen(true);
  };

  const handleCloseEditProgramModal = () => {
    refetchParticipant();
    refetchEnrollments();
    if (selectedEnrollment?._id) {
      queryClient.invalidateQueries({
        queryKey: ["coyn-test-results", selectedEnrollment._id],
      });
    }
    setSelectedEnrollment(undefined);
    setIsEditProgramModalOpen(false);
  };

  return (
    <Card sx={{ mt: 2, borderRadius: 2, minWidth: 0, overflow: "hidden" }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        <Typography variant="headlineMedium" fontWeight="bold">
          Program enrollment
        </Typography>
        {isLoadingEnrollments && (
          <>
            <Skeleton
              data-testid="skeleton"
              variant="text"
              width={"100%"}
              height={60}
            />
            <Skeleton
              data-testid="skeleton"
              variant="text"
              width={"100%"}
              height={60}
            />
          </>
        )}
        {enrollments?.length === 0 && (
          <Box
            onClick={handleEnrollToProgram}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 1,
              border: `1px dashed ${theme.palette.accent.main}`,
              padding: 2,
              width: "100%",
              height: "50px",
              cursor: handleEnrollToProgram ? "pointer" : "default",
              "&:hover": handleEnrollToProgram
                ? {
                    backgroundColor: theme.palette.surface.main,
                  }
                : {},
            }}
          >
            <AddBoxOutlinedIcon sx={{ color: theme.palette.accent.main }} />
          </Box>
        )}
        {enrollments
          ?.slice()
          .sort((a, b) => {
            // AC01: Sort by enrollment date (most recent first)
            const dateA = a.enrollmentDate
              ? new Date(a.enrollmentDate).getTime()
              : a.createdAt
                ? new Date(a.createdAt).getTime()
                : 0;
            const dateB = b.enrollmentDate
              ? new Date(b.enrollmentDate).getTime()
              : b.createdAt
                ? new Date(b.createdAt).getTime()
                : 0;
            return dateB - dateA;
          })
          .map((enrollment) => (
            <EnrollmentCard
              key={enrollment._id}
              enrollment={enrollment}
              handleDisenroll={handleDisenroll}
              handleViewImages={handleViewImages}
              onRefetchTestResults={(refetchFn) => {
                onRefetchTestResults(refetchFn);
              }}
              participantName={participantName || ""}
              handleEditEnrollment={handleEditEnrollment}
              handleOnDemandIncentive={handleOnDemandIncentive}
              isSingleEnrollment={enrollments.length === 1}
            />
          ))}
      </CardContent>
      {isDisenrollProgramModalOpen ? (
        <DisenrollProgramModal
          open={isDisenrollProgramModalOpen}
          onClose={handleCloseDisenrollProgramModal}
          participantName={participantName}
          enrollment={selectedEnrollment}
        />
      ) : null}

      {isEditProgramModalOpen ? (
        <ParticipantEditProgramModal
          open={isEditProgramModalOpen}
          onClose={handleCloseEditProgramModal}
          enrollment={selectedEnrollment}
        />
      ) : null}
    </Card>
  );
};

interface EnrollmentCardProps {
  enrollment: Enrollment;
  handleDisenroll: (enrollment: Enrollment) => void;
  handleViewImages: (
    testResultId: string,
    enrollmentId: string,
    enrollmentStatus: EnrollmentStatus,
    validationType: string,
  ) => void;
  onRefetchTestResults: (refetchFn: () => void) => void;
  participantName: string;
  handleEditEnrollment: (enrollment: Enrollment) => void;
  handleOnDemandIncentive: (enrollment: Enrollment) => void;
  isSingleEnrollment: boolean;
}

const EnrollmentCard: React.FC<EnrollmentCardProps> = ({
  enrollment,
  handleDisenroll,
  handleViewImages,
  onRefetchTestResults,
  participantName,
  handleEditEnrollment,
  handleOnDemandIncentive,
  isSingleEnrollment,
}) => {
  const { data: settings } = useGetSettings();
  const { program, status } = enrollment;
  const theme = useAppTheme();
  const { t } = useTranslation("clients");
  const { userHasAccessToCode } = useUserStore();
  // AC00-B: Check if user has permission to view results
  const canViewResults = userHasAccessToCode("PARTICIPANT_VIEW_RESULTS");

  // AC01: Auto-expand if single enrollment and user has permissions
  const [expanded, setExpanded] = useState(() => {
    // Only auto-expand if single enrollment and user has permissions
    return isSingleEnrollment && canViewResults;
  });

  // AC01: Update expanded state when enrollments change
  useEffect(() => {
    if (isSingleEnrollment && canViewResults) {
      setExpanded(true);
    } else if (!isSingleEnrollment) {
      // AC01: If multiple enrollments, keep collapsed and don't load data
      setExpanded(false);
    }
  }, [isSingleEnrollment, canViewResults]);

  const countrySetting = useCountrySetting();
  const currency = useCurrencySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);
  const locale = useMemo(() => getLocaleByCountry(country), [countrySetting]);

  // AC02: Get org unit name from enrollment.organization
  const orgUnitName = useMemo(() => {
    return enrollment.organization?.name || "";
  }, [enrollment.organization?.name]);

  // Get form factor display names from program configuration
  const getFormFactorName = (formFactorType: string | undefined) => {
    if (!formFactorType || !program?.configuration?.formFactors) return "N/A";
    const formFactor = program.configuration.formFactors.find(
      (ff) => ff.type === formFactorType,
    );
    return formFactor?.name || formFactorType || "N/A";
  };

  const formFactor1Name = useMemo(
    () => getFormFactorName(enrollment.formFactor1),
    [enrollment.formFactor1, program?.configuration?.formFactors],
  );

  const formFactor2Name = useMemo(
    () => getFormFactorName(enrollment.formFactor2),
    [enrollment.formFactor2, program?.configuration?.formFactors],
  );

  // AC02: Format title as "Program Name (Org Unit name)"
  const cardTitle = useMemo(() => {
    if (!program?.name) return "";
    return orgUnitName ? `${program.name} (${orgUnitName})` : program.name;
  }, [program?.name, orgUnitName]);

  // AC03: Format program duration "DD MMM YYYY to DD MMM YYYY"
  const programDuration = useMemo(() => {
    if (!program?.parameters?.startDate) return "";
    const startDate = formatInBackofficeLondon(
      program.parameters.startDate,
      "DD MMM YYYY",
    );
    const endDate = program.parameters.endDate
      ? formatInBackofficeLondon(program.parameters.endDate, "DD MMM YYYY")
      : "";
    return endDate ? `${startDate} to ${endDate}` : startDate;
  }, [program?.parameters?.startDate, program?.parameters?.endDate]);

  // AC05: Get status chip colors
  const getStatusChipColors = (status: EnrollmentStatus) => {
    switch (status) {
      case EnrollmentStatus.ACTIVE:
        return theme.palette.chipStatus.successful; // Green
      case EnrollmentStatus.COMPLETED:
        return theme.palette.chipStatus.default; // Gray
      case EnrollmentStatus.DISENROLLED:
        return theme.palette.chipStatus.failed; // Orange
      case EnrollmentStatus.PENDING_APPROVAL:
        return theme.palette.chipStatus.pendingApproval; // Orange
      case EnrollmentStatus.DENIED:
        return theme.palette.chipStatus.missed; // Red
      default:
        return theme.palette.chipStatus.default; // Gray
    }
  };

  // AC06: Calculate balances from transactions
  const balances = useMemo(() => {
    const transactions = enrollment.balance?.transactions || [];
    let earned = 0;
    let redeemed = 0;

    transactions.forEach((transaction) => {
      if (transaction.type === TransactionType.EARNED) {
        earned += transaction.amount;
      } else if (transaction.type === TransactionType.REDEEMED) {
        // Backend stores REDEEMED amounts as negative; use abs for display and reward calculation
        redeemed += Math.abs(transaction.amount);
      }
    });

    const reward = earned - redeemed; // Available balance

    return {
      earned,
      redeemed,
      reward: reward > 0 ? reward : 0,
    };
  }, [enrollment.balance?.transactions]);

  // AC05: Format CM end date according to status
  // COYN-1171: Italic/gray only for Expected values; standard for actual dates or N/A
  const formatCMEndDate = () => {
    if (status === EnrollmentStatus.DISENROLLED) {
      return { text: "N/A", isExpected: false };
    }
    if (status === EnrollmentStatus.ACTIVE && enrollment.cmStartDate) {
      const programDays = program?.parameters?.programDays || 0;
      const expectedDate = formatInBackofficeLondon(
        dayjs
          .utc(enrollment.cmStartDate)
          .add(programDays, "days")
          .toISOString(),
        "DD MMM YYYY",
      );
      return { text: `(Expected: ${expectedDate})`, isExpected: true };
    }
    if (status === EnrollmentStatus.COMPLETED && enrollment.cmEndDate) {
      return {
        text: formatInBackofficeLondon(enrollment.cmEndDate, "DD MMM YYYY"),
        isExpected: false,
      };
    }
    return { text: "N/A", isExpected: false };
  };

  const cmEndDateDisplay = formatCMEndDate();

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const cardIds =
    (
      settings?.qlikEnrollmentCardsConfig as any
    )?.actionsAndCards[0]?.cards?.map((c: any) => c?.cardId) || [];

  return (
    <Card
      sx={{
        mt: 2,
        mb: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.surface.main,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          pb: "0 !important",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ minWidth: 0, gap: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* AC02: Title format "Program Name (Org Unit name)" */}
            <Typography
              variant="titleSmall"
              fontWeight="bold"
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {cardTitle}
            </Typography>
            {/* AC03: Program duration */}
            {programDuration && (
              <Typography
                variant="bodyMedium"
                sx={{
                  color: theme.palette.textMain.main2,
                }}
              >
                {programDuration}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {/* AC08: Menu icon */}
            <EnrollmentMenu
              handleEditEnrollment={() => handleEditEnrollment(enrollment)}
              handleDisenroll={() => handleDisenroll(enrollment)}
              handleOnDemandIncentive={() =>
                handleOnDemandIncentive(enrollment)
              }
              theme={theme}
              enrollmentStatus={enrollment.status}
              enrollment={enrollment}
            />
          </Box>
        </Box>

        {/* AC04: Three information blocks */}
        <Grid container spacing={0.8} sx={{ minWidth: 0 }}>
          {/* AC04 & AC05: Enrollment information block */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
            <Card
              sx={{
                backgroundColor: "#fff",
                p: 2,
                height: "100%",
                minWidth: 0,
              }}
            >
              <Stack spacing={0.8}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Enrollment status:
                  </Typography>
                  <Chip
                    label={toTitleCase(formatEnrollmentStatus(status))}
                    size="small"
                    sx={{
                      backgroundColor: getStatusChipColors(status).fill,
                      borderColor: getStatusChipColors(status).border,
                      borderWidth: 1,
                      borderStyle: "solid",
                      width: "fit-content",
                    }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Enrollment date:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {enrollment.enrollmentDate
                      ? formatInBackofficeLondon(
                          enrollment.enrollmentDate,
                          "DD MMM YYYY",
                        )
                      : "N/A"}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    CM start date:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {enrollment.cmStartDate
                      ? formatInBackofficeLondon(
                          enrollment.cmStartDate,
                          "DD MMM YYYY",
                        )
                      : "N/A"}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    CM end date:
                  </Typography>
                  <Typography
                    variant="bodyLarge"
                    fontStyle={
                      cmEndDateDisplay.isExpected ? "italic" : "normal"
                    }
                    sx={
                      cmEndDateDisplay.isExpected
                        ? { color: theme.palette.textMain.main2 }
                        : undefined
                    }
                  >
                    {cmEndDateDisplay.text}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="bodyLarge"
                    fontWeight="bold"
                    sx={{ flexShrink: 0 }}
                  >
                    Assigned CM Coordinator:
                  </Typography>
                  <Typography
                    variant="bodyLarge"
                    sx={{ wordBreak: "break-word", minWidth: 0 }}
                  >
                    {enrollment.cmCoordinatorUser?.firstName &&
                    enrollment.cmCoordinatorUser?.lastName
                      ? `${enrollment.cmCoordinatorUser.firstName} ${enrollment.cmCoordinatorUser.lastName}`
                      : "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* AC04 & AC06: Incentive balances block */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
            <Card
              sx={{
                backgroundColor: "#fff",
                p: 2,
                height: "100%",
                minWidth: 0,
              }}
            >
              <Stack spacing={0.8}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Earned balance:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {formatCurrency(balances.earned, currency.currency, locale)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Reward balance:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {formatCurrency(balances.reward, currency.currency, locale)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Redeemed balance:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {formatCurrency(
                      balances.redeemed,
                      currency.currency,
                      locale,
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* AC04 & AC07: Naloxone information block */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
            <Card
              sx={{
                backgroundColor: "#fff",
                p: 2,
                height: "100%",
                minWidth: 0,
              }}
            >
              <Stack spacing={0.8}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Form factor 1:
                  </Typography>
                  <Typography variant="bodyLarge">{formFactor1Name}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Form factor 2:
                  </Typography>
                  <Typography variant="bodyLarge">{formFactor2Name}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="bodyLarge" fontWeight="bold">
                    Naloxone ID:
                  </Typography>
                  <Typography variant="bodyLarge">
                    {enrollment.naloxoneId || "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
        <Collapse in={expanded}>
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              minHeight: 380,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="enrollment-data"
                sx={{
                  borderBottom: "1px solid #C6C2DF",
                  "& .MuiTab-root": {
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                }}
              >
                <Tab label="Results" {...a11yProps(0)} />
                {/* <Tab label="Incentive history" {...a11yProps(1)} />
                <Tab label="Statistics" {...a11yProps(2)} /> */}
              </Tabs>
            </Box>
            <TabPanel value={selectedTab} index={0}>
              {/* AC00-B: Only show Results tab if user has permission */}
              {canViewResults ? (
                <TestResultsTab
                  enrollmentId={enrollment._id}
                  handleViewImages={handleViewImages}
                  onRefetchTestResults={onRefetchTestResults}
                  programName={program?.name || ""}
                  participantName={participantName}
                  enrollmentStatus={status}
                  shouldLoadData={expanded}
                />
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="bodyMedium" color="text.secondary">
                    {t("client_profile.insufficient_permissions_execute")}
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
              <IncentiveHistoryTab
                enrollment={enrollment}
                participantName={participantName}
                programName={program?.name || ""}
              />
            </TabPanel>
            <TabPanel value={selectedTab} index={2}>
              {cardIds ? (
                <StatisticsEmbeds
                  objectIds={cardIds}
                  enrollmentId={enrollment._id}
                  participantId={enrollment.participantId || ""}
                />
              ) : (
                <></>
              )}
            </TabPanel>
          </Box>
        </Collapse>
        {/* AC00-B: Only show expand button if user has permission to view results */}
        {canViewResults && (
          <Box display="flex" justifyContent="center">
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
export default EnrollmentList;
